<script setup>
  import { computed } from "vue";

  const props = defineProps({
    currentPage: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    lastPage: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    maxButtons: {
      type: Number,
      default: 7
    },
    showSummary: {
      type: Boolean,
      default: true
    }
  });

  const emit = defineEmits(["update:currentPage", "change"]);

  const isFirstPage = computed(() => {
    return props.currentPage <= 1;
  });

  const isLastPage = computed(() => {
    return props.currentPage >= props.lastPage;
  });

  const pagesToShow = computed(() => {
    const total = props.lastPage;
    const current = props.currentPage;
    const max = Math.max(3, props.maxButtons);
    const pages = [];

    if (total <= max) {
      for (let i = 1; i <= total; i++) {
        pages.push({ key: `p-${i}`, number: i, ellipsis: false });
      }
      return pages;
    }

    const sideCount = Math.floor((max - 3) / 2);
    let start = Math.max(2, current - sideCount);
    let end = Math.min(total - 1, current + sideCount);

    const visibleCount = 1 + (end - start + 1) + 1;
    if (visibleCount < max) {
      const missing = max - visibleCount;
      if (current <= 1 + sideCount + 1) {
        end = Math.min(total - 1, end + missing);
      } else if (current >= total - sideCount - 1) {
        start = Math.max(2, start - missing);
      }
    }

    pages.push({ key: "p-1", number: 1, ellipsis: false });

    if (start > 2) {
      pages.push({ key: "e-left", number: null, ellipsis: true });
    }

    for (let i = start; i <= end; i++) {
      pages.push({ key: `p-${i}`, number: i, ellipsis: false });
    }

    if (end < total - 1) {
      pages.push({ key: "e-right", number: null, ellipsis: true });
    }

    pages.push({ key: `p-${total}`, number: total, ellipsis: false });

    return pages;
  });

  const goToPage = (page) => {
    const target = Math.min(Math.max(1, page), props.lastPage);
    if (target === props.currentPage) return;
    const url = new URL(window.location);
    url.searchParams.set("page", target);
    window.history.pushState({}, "", url);

    emit("update:currentPage", target);
    emit("change", target);
  };
</script>

<template>
  <nav v-if="lastPage > 1" aria-label="Pagination" class="d-flex justify-content-center flex-column align-items-center">
    <ul class="pagination mb-0">
      <li class="page-item" :class="{ disabled: isFirstPage }">
        <a class="page-link" href="#" aria-label="First page" @click.prevent="goToPage(1)">
          <span aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" viewBox="0 0 10 15" fill="none">
             <path d="M0.489156 0.141999H1.49716V14.058H0.489156V0.141999ZM3.20713 8.248V7.464L9.92713
             3.712V4.804L4.38313 7.856L9.92713 10.908V12L3.20713 8.248Z" fill="#999999"/>
            </svg>
          </span>
        </a>
      </li>

      <li class="page-item" :class="{ disabled: isFirstPage }">
        <a class="page-link" href="#" aria-label="Previous" @click.prevent="goToPage(props.currentPage - 1)">
          <span aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
              <path d="M0.498141 5.248V4.464L7.21814 0.712V1.804L1.67414 4.856L7.21814 7.908V9L0.498141 5.248Z"
                    fill="#999999" />
            </svg>
          </span>
        </a>
      </li>

      <li
        v-for="page in pagesToShow"
        :key="page.key"
        class="page-item"
        :class="{ active: page.number === props.currentPage, disabled: page.ellipsis }"
      >
        <a
          v-if="!page.ellipsis"
          class="page-link"
          href="#"
          @click.prevent="goToPage(page.number)"
        >
          {{ page.number }}
        </a>
        <span v-else class="page-link">…</span>
      </li>

      <li class="page-item" :class="{ disabled: isLastPage }">
        <a class="page-link" href="#" aria-label="Next" @click.prevent="goToPage(props.currentPage + 1)">
          <span aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
              <path d="M0.778141 7.908L6.32214 4.856L0.778141 1.804V0.712L7.49814 4.464V5.248L0.778141 9V7.908Z"
                    fill="#999999" />
            </svg>
          </span>
        </a>
      </li>

      <li class="page-item" :class="{ disabled: isLastPage }">
        <a class="page-link" href="#" aria-label="Last page" @click.prevent="goToPage(props.lastPage)">
          <span aria-hidden="true">
           <svg xmlns="http://www.w3.org/2000/svg" width="10" height="15" viewBox="0 0 10 15" fill="none">
            <path d="M0.0691563 10.908L5.61316 7.856L0.0691563 4.804V3.712L6.78916 7.464V8.248L0.0691563
            12V10.908ZM8.50088 0.141999H9.50888V14.058H8.50088V0.141999Z" fill="#999999"/>
           </svg>
          </span>
        </a>
      </li>
    </ul>

    <small class="text-muted d-block mt-1" v-if="props.showSummary">
      Page {{ props.currentPage }} of {{ props.lastPage }} • Total: {{ props.total }} • Per page: {{ props.perPage }}
    </small>
  </nav>
</template>


<style scoped>
  .pagination {
    flex-wrap: wrap;

    .page-item {
      height: 40px;
      width: 40px;
      display: flex;
      justify-content: center;
      align-items: center;

      &.active {
        .page-link {
          background-color: #F5F5F5;
        }
      }

      .page-link {
        color: #999999;
        border: none;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: none;

        &:hover {
          background-color: #F5F5F5;
        }
      }
    }

  }

  .text-muted {
    margin-top: 10px;
    font-size: 14px;
    font-family: Bai Jamjuree, serif;
  }
</style>
